// EXtERNAL DEPENDENCIES
import React from 'react'

// HELPERS
import { trackClick } from '@/utils'
import { useModularSellerViewPageContent } from '@/screens/business-profile-page/hooks'
import { useImpressionTracking } from '@/hooks'

// FPTI
import {
  BP_EDIT_EXIT_SHEET_SHOWN_FPTI,
  CANCEL_EXIT_EDIT_BUTTON_CLICKED,
  CONFIRM_EXIT_EDIT_BUTTON_CLICKED,
} from './fpti'

// TEST IDS
import { CONFIRMATION_BOX_CONTAINER_TEST_ID } from '@/constants/test-ids'

// COMPONENTS
import { Button, Dialog, HeadingText } from '@paypalcorp/pp-react'

// STYLES
import styles from './styles.module.less'

// TYPES
import { UnformattedFptiOptions } from '@/types'
type ExitConfirmationBoxProps = {
  isOpen: boolean
  fpti: UnformattedFptiOptions
  handleBack: () => void
  closeConfirmationDialog: () => void
}

/**
 * Displays a confirmation dialog popup confirming if the user
 * wants to exit or not
 */
function ExitConfirmationBox({
  isOpen,
  handleBack,
  closeConfirmationDialog,
  fpti,
}: ExitConfirmationBoxProps) {
  useImpressionTracking({ ...BP_EDIT_EXIT_SHEET_SHOWN_FPTI, ...fpti })

  // INITIALIZE CONTENT
  const useContent = useModularSellerViewPageContent()
  const exitConfirmationTitle = useContent('confirmationHeader')
  const exitButtonText = useContent('exitButtonText')
  const continueEditingButtonText = useContent('continueEditingButtonText')

  const handleConfirmAndClose = () => {
    trackClick({ ...CONFIRM_EXIT_EDIT_BUTTON_CLICKED, ...fpti })
    closeConfirmationDialog()
    handleBack()
  }

  const handleCancelAndClose = () => {
    trackClick({ ...CANCEL_EXIT_EDIT_BUTTON_CLICKED, ...fpti })
    closeConfirmationDialog()
  }

  const handleOnCloseComplete = (_e: unknown, closedBy: string) => {
    if (closedBy !== 'caller') {
      handleCancelAndClose()
    }
  }

  return (
    <Dialog
      center
      onCloseComplete={handleOnCloseComplete}
      isOpen={isOpen}
      title={exitConfirmationTitle}
      hideTitle
      data-testid={CONFIRMATION_BOX_CONTAINER_TEST_ID}
    >
      <div className={styles.confirmationBoxContainer}>
        <HeadingText level={3} className={styles.confirmationBoxHeaderText}>
          {exitConfirmationTitle}
        </HeadingText>

        <div className={styles.confirmationBoxButtonsContainer}>
          <Button
            onClick={handleConfirmAndClose}
            className={styles.confirmationBoxExitButton}
          >
            {exitButtonText}
          </Button>
          <Button tertiary onClick={handleCancelAndClose}>
            {continueEditingButtonText}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

export default ExitConfirmationBox
